import axios, {AxiosError, AxiosInstance, InternalAxiosRequestConfig} from 'axios'
import Swal from 'sweetalert2'
import {AuthModel} from './_models'
import {TOKEN_REFRESH_URL} from './_requests'
import {getAuth, setAuth, removeAuth} from './AuthHelpers'
import {decryptResponse} from '../../../utils/decrypt'

/** Axios instance without auth interceptors — used only for refresh POST */
const refreshHttp = axios.create({
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

let refreshPromise: Promise<string> | null = null

function resolveUrl(config: InternalAxiosRequestConfig): string {
  const base = config.baseURL ?? ''
  const path = config.url ?? ''
  return `${base}${path}`
}

function is401RefreshExempt(url: string): boolean {
  const u = url.toLowerCase()
  return (
    u.includes('/users/auth/token/refresh/') ||
    u.includes('/users/admin/auth/login/') ||
    u.includes('/users/admin/auth/signup/')
  )
}

/**
 * Reads refresh token from primary auth blob (kt-auth) or legacy keys set at login.
 */
export function getRefreshTokenFromStorage(): string | undefined {
  try {
    const auth = getAuth() as AuthModel & {refresh_token?: string}
    const fromAuth =
      (typeof auth?.refresh === 'string' && auth.refresh) ||
      (typeof auth?.refresh_token === 'string' && auth.refresh_token)
    if (fromAuth) {
      return fromAuth
    }
  } catch {
    /* ignore */
  }
  const legacy = localStorage.getItem('refresh_token')
  return legacy?.trim() || undefined
}

/**
 * Persists new access token in kt-auth blob and legacy `access_token` key.
 */
export function persistNewAccessToken(newAccess: string): void {
  const auth = getAuth()
  if (auth) {
    const next: AuthModel = {
      ...auth,
      access_token: newAccess,
      token: newAccess,
    }
    setAuth(next)
  }
  localStorage.setItem('access_token', newAccess)
  localStorage.setItem('token', newAccess)
}

export function clearAuthSession(): void {
  removeAuth()
  localStorage.removeItem('access_token')
  localStorage.removeItem('token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('user_type')
  localStorage.removeItem('user')
  try {
    localStorage.removeItem('developer_profile')
  } catch {
    /* ignore */
  }
}

/**
 * Clears all auth storage and sends the user to the login route (full navigation).
 */
export function logoutAndRedirectToLogin(): void {
  clearAuthSession()
  const prefix = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
  window.location.assign(`${prefix}/auth/login`)
}

/**
 * Calls the refresh API, updates storage with the new access token, returns it.
 * On failure: clears auth and redirects to /login.
 */
export async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    try {
      const refresh = getRefreshTokenFromStorage()
      if (!refresh) {
        throw new Error('No refresh token')
      }

      const {data} = await refreshHttp.post<{access: string}>(TOKEN_REFRESH_URL, {
        refresh,
      })

      if (!data?.access) {
        throw new Error('Invalid refresh response')
      }

      persistNewAccessToken(data.access)
      return data.access
    } catch (err) {
      logoutAndRedirectToLogin()
      throw err
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

type RetryableConfig = InternalAxiosRequestConfig & {_retry401?: boolean}

function attachBearer(config: InternalAxiosRequestConfig) {
  const auth = getAuth()
  const token = auth?.access_token || (auth as AuthModel | undefined)?.token
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
}

function showApiErrorAlert(error: AxiosError) {
  // If no response or it's a 401 (which is handled by refresh flow), do not show alert here
  if (!error.response || error.response.status === 401) {
    return
  }

  // Extract error detail
  const detail = error.response.data && (error.response.data as any).detail
  let errorMessage = ''
  if (detail) {
    if (typeof detail === 'string') {
      errorMessage = detail
    } else if (Array.isArray(detail)) {
      errorMessage = detail.map((err: any) => `${err.loc?.join('.') || 'Error'}: ${err.msg || JSON.stringify(err)}`).join(', ')
    } else {
      errorMessage = JSON.stringify(detail)
    }
  } else {
    errorMessage = (error.response.data as any)?.message || error.message || 'An error occurred during request.'
  }

  const isPermissionError = error.response.status === 403 || errorMessage.toLowerCase().includes('permission')

  if (isPermissionError) {
    window.dispatchEvent(
      new CustomEvent('api-permission-error', {
        detail: { message: errorMessage }
      })
    )
    return
  }

  // Alert configuration for other errors
  Swal.fire({
    icon: 'error',
    title: 'API Error',
    text: errorMessage,
    confirmButtonColor: '#ef4444',
  })
}

/**
 * Request + response interceptors: Bearer header, 401 → refresh once → retry.
 */
export function attachAuthenticatedAxios(instance: AxiosInstance) {
  instance.interceptors.request.use(
    (config) => {
      attachBearer(config)
      return config
    },
    (err) => Promise.reject(err)
  )

  instance.interceptors.response.use(
    async (res) => {
      if (res.data) {
        res.data = await decryptResponse(res.data)
      }
      return res
    },
    async (error: AxiosError) => {
      const original = error.config as RetryableConfig | undefined
      if (!original || original._retry401) {
        showApiErrorAlert(error)
        return Promise.reject(error)
      }

      if (error.response?.status !== 401) {
        showApiErrorAlert(error)
        return Promise.reject(error)
      }

      const url = resolveUrl(original)
      if (is401RefreshExempt(url)) {
        showApiErrorAlert(error)
        return Promise.reject(error)
      }

      const hadBearer = !!original.headers?.Authorization
      const refreshTok = getRefreshTokenFromStorage()

      if (!refreshTok) {
        if (hadBearer) {
          logoutAndRedirectToLogin()
        }
        showApiErrorAlert(error)
        return Promise.reject(error)
      }

      original._retry401 = true

      try {
        const newAccess = await refreshAccessToken()
        if (original.headers) {
          original.headers.Authorization = `Bearer ${newAccess}`
        }
        return instance.request(original)
      } catch (err) {
        showApiErrorAlert(error)
        return Promise.reject(error)
      }
    }
  )
}

/**
 * Global axios defaults + interceptors (used by `axios` import across the app).
 */
export function setupAxios(axiosStatic: typeof axios) {
  axiosStatic.defaults.headers.Accept = 'application/json'
  attachAuthenticatedAxios(axiosStatic as unknown as AxiosInstance)
}

/**
 * `fetch` helper: attaches Bearer, retries once after refresh on 401.
 */
export async function authenticatedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const auth = getAuth()
  const token = auth?.access_token || auth?.token
  const headers = new Headers(init.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const doFetch = () => fetch(input, {...init, headers})

  let res = await doFetch()
  if (res.status !== 401 || !getRefreshTokenFromStorage()) {
    return res
  }

  try {
    const newAccess = await refreshAccessToken()
    headers.set('Authorization', `Bearer ${newAccess}`)
    res = await fetch(input, {...init, headers})
  } catch {
    /* refreshAccessToken already redirected */
  }
  return res
}
